import { useStores } from '@/hooks/useStores';
import { useActiveModels } from '@/hooks/useModels';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { bookingFormSchema, type BookingFormData } from '@geely/shared';
import { Button } from '@geely/ui';
import { ArrowLeft } from 'lucide-react';
import { useBookingWizard } from './hooks/useBookingWizard';
import { useRescheduleDetection } from './hooks/useRescheduleDetection';
import { useBookingSubmit } from './hooks/useBookingSubmit';
import { HOME_URL } from '@/lib/constants';
import {
  BookingHeader,
  BookingProgress,
  StoreSelectionStep,
  PersonalInfoStep,
  VehicleInfoStep,
  ReviewStep,
  ConfirmationStep,
} from './components';

export function BookingPage() {
  const { data: stores = [], isLoading: isLoadingStores } = useStores();
  const { data: models = [] } = useActiveModels();

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      customerCpf: '',
      vehiclePlate: '',
      vehicleVin: undefined,
      vehicleMileage: undefined as any,
      modelId: '',
      versionId: '',
      lgpdConsent: false,
    },
    mode: 'onBlur',
  });

  const wizard = useBookingWizard(stores);
  const { isRescheduling, originalAppointmentId } = useRescheduleDetection(form.reset);

  const {
    conflictError,
    isSubmitting,
    copied,
    handleSubmit,
    handleCopyId,
    clearConflictError,
  } = useBookingSubmit({
    selectedStoreId: wizard.selectedStoreId,
    selectedDate: wizard.selectedDate,
    selectedTime: wizard.selectedTime,
    getFormValues: form.getValues,
    isRescheduling,
    originalAppointmentId,
    setStep: wizard.setStep,
    setSubStep: wizard.setSubStep,
    setSelectedTime: wizard.setSelectedTime,
    setAppointmentId: wizard.setAppointmentId,
  });

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <a href={HOME_URL} target="_top">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="size-4 mr-2" />
            Voltar
          </Button>
        </a>

        <BookingHeader step={wizard.step} subStep={wizard.subStep} isRescheduling={isRescheduling} />
        <BookingProgress currentStep={wizard.step} />

        {wizard.step === 1 && (
          <StoreSelectionStep
            subStep={wizard.subStep}
            stores={stores}
            isLoadingStores={isLoadingStores}
            selectedState={wizard.selectedState}
            selectedCity={wizard.selectedCity}
            selectedStoreId={wizard.selectedStoreId}
            selectedDate={wizard.selectedDate}
            selectedTime={wizard.selectedTime}
            conflictError={conflictError}
            onStateSelect={wizard.handleStateSelect}
            onCitySelect={wizard.handleCitySelect}
            onStoreSelect={wizard.handleStoreSelect}
            onDateTimeSelect={wizard.handleDateTimeSelect}
            onBackSubStep={wizard.handleBackSubStep}
            onNext={() => wizard.handleNext()}
            onSelectAlternativeTime={(time) => {
              wizard.setSelectedTime(time);
              clearConflictError();
            }}
          />
        )}

        {wizard.step === 2 && (
          <PersonalInfoStep
            form={form}
            onBack={wizard.handleBack}
            onNext={() => wizard.handleNext(async () => form.trigger(['customerName', 'customerPhone', 'customerEmail', 'customerCpf']))}
          />
        )}

        {wizard.step === 3 && (
          <VehicleInfoStep
            form={form}
            onBack={wizard.handleBack}
            onNext={() => wizard.handleNext(async () => form.trigger(['vehiclePlate', 'vehicleVin', 'vehicleMileage', 'modelId', 'versionId']))}
          />
        )}

        {wizard.step === 4 && wizard.selectedStore && (
          <ReviewStep
            selectedStore={wizard.selectedStore}
            selectedDate={wizard.selectedDate}
            selectedTime={wizard.selectedTime}
            form={form}
            models={models}
            isSubmitting={isSubmitting}
            isRescheduling={isRescheduling}
            onBack={wizard.handleBack}
            onSubmit={handleSubmit}
          />
        )}

        {wizard.step === 5 && wizard.selectedStore && (
          <ConfirmationStep
            appointmentId={wizard.appointmentId}
            selectedStore={wizard.selectedStore}
            selectedDate={wizard.selectedDate}
            selectedTime={wizard.selectedTime}
            isRescheduling={isRescheduling}
            copied={copied}
            onCopy={() => handleCopyId(wizard.appointmentId)}
          />
        )}
      </div>
    </div>
  );
}
